import { Component, Injectable, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, AsyncValidator, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, catchError, map, of, startWith, switchMap } from 'rxjs';
import { commonModules } from 'src/app/app.config';
import { ConfirmationConfig, ConfirmationDialogComponent } from 'src/app/components/dialog/confirmation-dialog/confirmation-dialog.component';
import { ToolbarComponent } from 'src/app/components/toolbar/toolbar.component';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { ApiError, User, UserAdd, UserRole, UserUpdate, UserVenue, VenueService } from 'src/app/planvue-api';
import { DisplayedVenue, UserForm, UserFormComponent } from './user-form.component';


import { ValidationErrors, ValidatorFn } from '@angular/forms';
import { ToolbarPageComponent } from 'src/app/components/base-classes/toolbarpage-component';
import { UserService } from 'src/app/planvue-api-extensions/UserService';

import { FieldControls, PlanVueAuthMatrix, SelfEditPermissions } from './permissions-matrix';

export const venuesRequiredForUserRole: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const role = control.get('role');
  const venues = control.get('venues');
  const dirty = control.dirty
  return dirty && role && venues && role.value === 'user' && (venues.value == null || venues.value.length === 0) ? { 'venuesRequired': true } : null;
};

@Injectable({ providedIn: 'root' })
export class UniqueEmailValidator implements AsyncValidator {
  private initialValue?: string
  constructor(private userService: UserService) {}

  setInitialValue(value: string) {
    this.initialValue = value;
  }

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    if (this.initialValue === control.value) {
      return of(null);
    }

    if (!control.value)
      return of({ nothing: true });
    return this.userService.checkEmailExists(control.value).pipe(
      map(() => { 
        return null; // No error.
      }),
      catchError((error: ApiError) => { 
        if (error.status === 409) {
          return of({ already_exists: true }); // Email already exists.
        }
        throw error;
      })
    );
  }
}

@Component({
  template: '',
})
export abstract class BaseUserFormComponent extends ToolbarPageComponent implements CanComponentDeactivate {
  protected readonly dialog = inject(MatDialog);
  protected readonly venueService = inject(VenueService)
  protected readonly userService = inject(UserService);
  protected readonly uniqueEmailValidator = inject(UniqueEmailValidator);
  protected venues: DisplayedVenue[] = []; // The venues assigned to the user.
  protected currentUser: User; // The current user.
  protected saveButtonLabel = "Save"; // The label for the save button.
  protected permitVenueAssignment = true;
  protected showAssignedVenues = true;
  protected allowDelete = true;
  protected allowSave = true;
  protected allowReset = true;
  protected allowSendReset = true;

  allowedRoles = [ { 
    label: "Lite",
    value: "lite"
  }, { 
    label: "Admin",
    value: "admin"
  }, { 
    label: "Power",
    value: "power"
  }, { 
    label: "User",
    value: "user"
  }]

  userForm: FormGroup<UserForm> = new FormGroup<UserForm>({
    first_name: new FormControl<string | null>('', { validators: [Validators.required], nonNullable: true }),
    last_name: new FormControl<string | null>('', { validators: [Validators.required], nonNullable: true }),
    email: new FormControl<string | null>(null, { validators: [Validators.required, Validators.email], asyncValidators: [this.uniqueEmailValidator.validate.bind(this.uniqueEmailValidator)] }),
    role: new FormControl<UserRole | null>("user", { validators: [Validators.required] }),
    organization: new FormControl<string | null>(null),
    active: new FormControl<boolean | null>(true),
    venues: new FormControl<[] | null>([]),
    expiration_date: new FormControl<Date | null>(null),
  }, { validators: [venuesRequiredForUserRole] });

  protected readonly DIRTY_CONFIRMATION_CONFIG: ConfirmationConfig = {
    title: "Unsaved Changes",
    message: "Would you like to continue and lose your changes?",
    confirmText: "Continue",
    cancelText: "No, Stay Here",
  };

  constructor() {
    super();
    this.currentUser = this.route.snapshot.data['currentUser'];
  }

  protected setupVenues(userVenues: UserVenue[] = []): void {
    this.venueService.getVenues().subscribe((venues) => {
        this.venues = venues.items.sort((a, b) => a.name.localeCompare(b.name)).
        map(v => {
          return {
            name: v.name,
            id: v.id!,
            selected: userVenues.some(uv => uv.id === v.id),
          };
        });
    });
  }

  canDeactivate(): Observable<boolean> {
    return this.userForm.valueChanges.pipe(
      startWith(null),
      takeUntilDestroyed(this.destroy),
      map(() => this.userForm.dirty), 
      switchMap((dirty) => {
      if (!dirty) return of(true);
          return this.dialog.open(ConfirmationDialogComponent, { data: this.DIRTY_CONFIRMATION_CONFIG }).afterClosed();
       })
    )
  }
  
  abstract save(): void;
  abstract delete(): void;
  send_reset_email(): void { }

  reset() {
    this.userForm.reset();
  }
}

@Component({
  selector: 'app-new-user',
  templateUrl: './user.component.html',
  standalone: true,
  imports: [...commonModules, UserFormComponent, ToolbarComponent],
  styleUrl: './user.component.scss'
})
export class NewUserComponent extends BaseUserFormComponent implements OnInit, CanComponentDeactivate {

  override ngOnInit(): void {
    super.ngOnInit();
    this.currentUser = this.route.snapshot.data['currentUser']
    this.setupVenues();
    this.saveButtonLabel = "Create"
    this.userForm.markAsPristine();
    this.allowDelete = false;
    this.allowSendReset = false;

    this.userForm.controls['role'].valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((role) => {
      if (this.currentUser.role === 'admin' && (role === 'admin' || role === 'power')) {
        this.permitVenueAssignment = false; 
        this.userForm.controls['venues'].setValue([]);
      } else {
        this.permitVenueAssignment = true;  
        this.userForm.controls['venues'].enable();
      }
    });

    if (this.currentUser.role === 'power') {
      this.allowedRoles = this.allowedRoles.filter(role => role.value !== 'admin' && role.value !== 'power');
    }
  
  }
  override delete(): void {
    throw new Error("Method not implemented")
  }

  override save(): void {
    const userAdd : UserAdd = { 
      first_name: this.userForm.value.first_name!,
      last_name: this.userForm.value.last_name!,
      email: this.userForm.value.email!,
      role: this.userForm.value.role!,
      organization: this.userForm.value.organization,
      active: this.userForm.value.active!,
      venues: this.userForm.value.venues,
      expiration_date: this.userForm.value.expiration_date ? this.userForm.value.expiration_date.toISOString() : null
    }

    this.userService.addUser(userAdd).pipe(catchError((error: ApiError) => {
      if (error.status === 409) {
        this.userForm.controls['email'].setErrors({ 'already_exists': true });
        return of(null)
      }
      throw error;
    })).
    subscribe((user: User | null) => {
      if (user) { 
        this.reset();
        this.notifySuccess(`User ${user.email} added successfully.`);
        this.router.navigate(['/user']);
      }
    });
  }
}



@Component({
  selector: 'app-edit-user',
  templateUrl: './user.component.html',
  standalone: true,
  imports: [...commonModules, ToolbarComponent, UserFormComponent],
  styleUrl: './user.component.scss'
})
export class EditUserComponent extends BaseUserFormComponent implements OnInit, CanComponentDeactivate {
  protected user! : User;

  override ngOnInit(): void {
    super.ngOnInit();
    this.user = this.route.snapshot.data['user'] as User;
    this.currentUser = this.route.snapshot.data['currentUser']
    this.uniqueEmailValidator.setInitialValue(this.user.email);
    this.saveButtonLabel = "Save"

    this.setupVenues(this.user.venues ?? []);

    this.userForm.controls['role'].valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((role) => {
      if (role === 'admin' || role === 'power') {
        this.permitVenueAssignment = false; 
        this.userForm.controls['venues'].setValue([]);
        this.userForm.controls['venues'].disable();
      } else {
        this.permitVenueAssignment = true;  
        this.userForm.controls['venues'].enable();
      }
    });

    this.reset();

    let targetRole : FieldControls
    if (this.user.id == this.currentUser.id) { 
      targetRole = SelfEditPermissions
    } else { 
      const sourceRole = PlanVueAuthMatrix[this.currentUser.role]

      if (sourceRole === undefined) { 
        throw new Error(`Role ${this.currentUser.role} not found in permissions matrix`)
      }
      
      targetRole = sourceRole[this.user.role]
  
      if (targetRole === undefined) { 
        throw new Error(`Role ${this.user.role} not found in permissions matrix`)
      }
    }

    this.allowDelete = targetRole.allowDeletion;
    this.allowSave = targetRole.allowSave;
    this.allowSendReset = targetRole.allowPasswordReset;

    for (const key in targetRole.fields) {
        const controlName = key as string
        targetRole.fields[key as keyof typeof targetRole.fields] === true ? this.userForm.controls[controlName as keyof UserForm].enable() : this.userForm.controls[controlName as keyof UserForm].disable();
    }
    this.allowedRoles = this.allowedRoles.filter(role => targetRole.permitted_roles.includes(role.value as UserRole));
  }

  override send_reset_email(): void { 
    const user_id = this.route.snapshot.data['user'].id!;
    this.userService.sendPasswordReset(user_id).subscribe(() => {
      this.notifySuccess("Password reset email has been sent.");
    });
  }


  override delete(): void { 
    const user_id = this.route.snapshot.data['user'].id!;
    this.userService.deleteUser(user_id).subscribe(() => {
      this.notifySuccess("User deleted successfully.");
      this.router.navigate(['/user']);
    });
  }

  getUpdate() {
    const dirtyValues: Partial<UserUpdate> = {};
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      if (control?.dirty) {
        (dirtyValues as any)[key] = control.value; // eslint-disable-line @typescript-eslint/no-explicit-any
      }
    });

    return dirtyValues as Partial<UserUpdate>;
  }

  override save(): void {
    const user_id = this.route.snapshot.data['user'].id!;
    this.userService.patchUser(user_id, this.getUpdate()).subscribe(() => {
      this.userForm.reset();
      this.notifySuccess("User updated successfully.");
      this.router.navigate(['/user']);
    });
  }

  override reset() : void { 
    this.userForm.reset( { 
      first_name: this.user.first_name,
      last_name: this.user.last_name,
      email: this.user.email,
      role: this.user.role,
      organization: this.user.organization,
      active: this.user.active,
      venues: this.user.venues?.map(v => v.id) ?? [],
      expiration_date: this.user.expiration_date ? new Date(this.user.expiration_date!) : null
    })
  }

}